<template>
  <Layout v-if="builderReady || portalReady">
    <div class="router-view footer-padding">
      <div class="products flex flex-wrap max-w-md mx-auto px-3 pb-3">
        <template v-if="availableProducts && availableProducts.length > 0">
          <!-- Offerred Rates -->
          <div
            v-for="product in availableProducts"
            :key="`offered-${product.key}`"
            :data-key="product.key"
            class="w-full"
          >
            <div v-for="rate in product.rates" :key="`offered-${rate.key}`" :data-key="rate.key">
              <div
                v-if="getRateComponent(rate.key) && rate.isOffered && !rate.activeOnAccount"
                class="shadow mb-3 rounded overflow-hidden"
              >
                <div class="bg-white relative py-3 pr-3 pl-4">
                  <Transition name="fade">
                    <div
                      v-if="isRateActive(rate.key)"
                      style="padding-left: 6px;"
                      class="h-full bg-green-500 absolute left-0 top-0"
                    />
                  </Transition>

                  <div v-if="contentLoading">
                    <DrLoader />
                  </div>

                  <Component :is="getRateComponent(rate.key)" :rate="rate" :teaser="true" />

                  <template>
                    <div v-if="!isRateActive(rate.key)" class="flex items-center justify-between flex-row-reverse">
                      <Transition name="fade" mode="out-in">
                        <div
                          v-if="getAddError(rate.key) && showError(rate.key)"
                          :key="`add-error-${rate.key}`"
                          class="text-xs text-red-650 inline-block"
                        >
                          {{ getAddError(rate.key) }}
                        </div>
                        <DrButton
                          v-else
                          key="addCoverButton"
                          :data-cy="`btn-add-cover-${rate.key}`"
                          class="btn btn-cyan btn-sm inline-block px-6"
                          @click="handleAddRate(rate.key)"
                        >
                          Add cover
                        </DrButton>
                      </Transition>
                      <div
                        v-if="isEssential(rate.key)"
                        :style="{ fontSize: '13px' }"
                        class="text-yellow-500 flex items-center"
                      >
                        <DrIcon svg="star" class="text-yellow-400" />
                        <span>Essential cover</span>
                      </div>
                    </div>
                    <div v-else class="flex items-center justify-between flex-row-reverse">
                      <RouterLink
                        class="btn btn-grey btn-sm px-6"
                        :to="`/products/${rate.key}/config`"
                        :data-cy="`btn-edit-cover-${rate.key}`"
                      >
                        Edit
                      </RouterLink>

                      <DrButton
                        v-if="isRateRemovable"
                        key="removeButton"
                        :data-cy="`btn-remove-${rate.key}`"
                        class="btn-sm btn-reset pl-3 text-blue-500"
                        @click.prevent="handleRemoveRate(rate.key)"
                      >
                        remove
                      </DrButton>
                    </div>
                  </template>
                </div>

                <CardFooter v-if="showCardFooter(rate.key)" :rate="rate" />
              </div>
            </div>
          </div>

          <!-- Unofferred Rates -->
          <div v-if="ratesUnoffered.length > 0">
            <h3 class="text-sm font-bold mt-5 mb-3">We need to chat before we can give you a quote for:</h3>
            <div
              v-for="product in availableProducts"
              :key="`unoffered-${product.key}`"
              :data-key="product.key"
              class="w-full"
            >
              <div v-for="rate in product.rates" :key="`unoffered-${rate.key}`" :data-key="rate.key">
                <div
                  v-if="getRateComponent(rate.key) && !rate.isOffered"
                  class="mb-3 py-3 pr-3 pl-4 relative shadow rounded overflow-hidden"
                  style="background-color: rgba(256, 256, 256, 0.45);"
                >
                  <div style="padding-left: 6px;" class="h-full bg-yellow-400 absolute left-0 top-0" />

                  <Component
                    :is="getRateComponent(rate.key)"
                    :rate="rate"
                    :teaser="true"
                    class="pl-1 select-none cursor-default opacity-50"
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="coupons.includes('WEWORK')"
            class="we-work-banner bg-green-500 p-3 rounded shadow m-4 flex items-center"
          >
            <img class="w-1/5" alt="WeWork" src="./../../../assets/img/content/we-work-logo.png">
            <span class="text-white font-bold text-left ml-4">
              WeWork members get a 10% discount. We've included this in your quote.
            </span>
          </div>

          <div class="mt-3 text-2xs text-center w-3/4 mx-auto">
            The premium below is an indication.<br>
            Click review quote to finalise your quote.
          </div>
        </template>
        <div v-else class="mx-auto">
          There are currently no products for this industry
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
// @ is an alias to /src
import getSlidesMixin from '@/mixins/getSlides'
import rateMixin from '@/mixins/rate'
// import TurnoverVue from '@/components/customer/Turnover.vue'
export default {
  name: 'ProductList',
  components: {
    Layout: () => import('@/router/layouts/Main'),
    CardFooter: () => import('@/components/products/CardFooter'),
  },
  mixins: [getSlidesMixin, rateMixin],
  data () {
    return {
      errors: {
        'AvivaCommercialCombined\\GeneralLiability': false,
        'AvivaCommercialCombined\\OfficeCover': false,
        'CommercialCombined\\GeneralLiability': false,
        'CommercialCombined\\OfficeCover': false,
        'ManagementLiability\\Crime': false,
        'CyberSecurity\\CyberSecurity': false,
        'ManagementLiability\\DirectorsAndOfficers': false,
        'ManagementLiability\\EmploymentPracticesLiability': false,
        'LegalExpenses\\LegalExpenses': false,
        'ProfessionalIndemnity\\ProfessionalIndemnity': false,
        'HealthcareProfessional\\HealthcareProfessional': false,
        'ProfessionalInsurance\\ProfessionalIndemnity': false,
        'ProfessionalInsurance\\CyberSecurity': false,
      },
      contentLoading: false,
    }
  },
  computed: {
    builderReady () {
      return this.$store.getters.getBuilderReady
    },
    portalReady () {
      return this.$store.getters.getPortalReady
    },
    ratesUnoffered () {
      return this.$store.getters.getRatesUnoffered
    },
    coupons () {
      return this.$store.getters.couponIds
    },
    availableProducts () {
      return this.$store.getters.getAvailableProducts
    },
    availableRates () {
      return this.$store.getters.getAvailableRates
    },
    products () {
      return this.$store.getters.getProducts
    },
    quoteStateUpdated: {
      get () {
        return this.$store.getters.getQuoteStateUpdated
      },
      set (value) {
        this.$store.commit(`updateQuoteStateUpdated`, value)
      },
    },
    productsContent: {
      get () {
        return this.$store.getters.getProductsContent
      },
      set (value) {
        return this.$store.commit('updateProductsContent', value)
      },
    },
  },
  beforeRouteEnter (to, from, next) {
    // if coming back to products from somewhere else in quote, check products are still available and update rates
    next((vm) => {
      // if quote state has changed (industry has changed), need to check if old rates still apply
      if (vm.quoteStateUpdated && vm.products.length > 0) {
        vm.updateRates()
      }

      if (vm.quoteStateUpdated) vm.quoteStateUpdated = false

      next()
    })
  },
  methods: {
    async updateRates () {
      // check if rate is available, if not, then remove
      const keys = []
      for (const product of this.products) {
        if (!this.isProductAvailable(product.key)) {
          for (const rate of product.rates) {
            keys.push(rate.key)
          }
        } else {
          for (const rate of product.rates) {
            if (
              !this.isRateAvailable(rate.key) ||
              !this.isRateActive(rate.key) ||
              !this.isRateOffered(rate.key) ||
              !this.validateAddRate(rate.key)
            ) {
              keys.push(rate.key)
            }
          }
        }
      }
      if (keys.length > 0) await this.removeRate(keys)
      else await this.retrieveEstimate({ products: this.$store.getters.getProducts })
      await this.$store.dispatch('updateQuote', this.$store.getters.getEstimate)
    },
    showCardFooter (key) {
      if (key.includes('GeneralLiability') && this.$store.getters.getEmployeeCount <= 1) return false
      const content = this.getRateContent(key)
      return content.more_info || content.footer_content
    },
    isEssential (key) {
      return key.includes('GeneralLiability') || key.includes('EmployersLiability') || key.includes('OfficeCover')
    },
    showError (key) {
      return this.errors[key] === true
    },
    getAddError (key) {
      // can't add EPL or Crime if D&O is inactive
      if (
        (key.includes('EmploymentPracticesLiability') || key.includes('Crime')) &&
        !this.isRateActive('ManagementLiability\\DirectorsAndOfficers')
      ) { return 'Can only be added if Directors and officers (D&O) is configured' }
      // can't add LE without other product
      if (
        key.includes('LegalExpenses') &&
        this.$store.getters.getProducts.length === 0 &&
        !this.$store.getters.isCouponWithFreeLegalEntered
      ) { return 'Must be purchased with at least 1 other cover' }
      return null
    },
    validateAddRate (key) {
      return this.getAddError(key) === null
    },
    async handleAddRate (key) {
      this.errors[key] = false
      if (!this.validateAddRate(key)) {
        this.errors[key] = true
        return
      }
      await this.getSlides(key)
    },
    async handleRemoveRate (key) {
      await this.removeRate(key)

      await this.$store.dispatch('updateQuote', this.$store.getters.getEstimate)
      this.$router.push('/products')
    },
  },
}
</script>
